import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { RoomMatchMockup } from '../components/visuals/RoomMatchMockup.js';
import { IconTrades } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-mechanics')({
  component: MechanicsPage,
});

function MechanicsPage() {
  usePageTitle('Bookings for independent mechanics & workshops');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For independent mechanics',
  icon: IconTrades,
  headline: 'Every car on the right hoist, on the right day.',
  headlineEmphasis: 'right hoist',
  subhead:
    'Bookaburra knows a 4WD logbook is two hours and a brake-pad swap is forty-five minutes — and only offers slots where the right hoist is free.',
  Visual: RoomMatchMockup,
  business: {
    name: 'Inner West Auto',
    location: 'Sydney Inner West · 2 mechanics · 2 hoists, 1 alignment bay',
    summary:
      "A fictional independent workshop in Sydney's Inner West. Two mechanics share two hoists; the alignment bay is a single-job-at-a-time bottleneck. Mix of capped-price logbook services, brake/tyre work, and a few fleet customers booked weekly for vans. Mondays are heavy as people drop cars in over the weekend.",
  },
  dayTimeline: [
    {
      time: '7:30',
      event:
        'Workshop opens. Hoist 1 has a 4WD logbook all morning (3 hours); hoist 2 has back-to-back brake/tyre jobs; alignment bay holds an 11am wheel-alignment.',
      bookaburraResponse:
        'Nothing to do. Every job blocked its actual duration and the right resource.',
    },
    {
      time: '8:45',
      event:
        'A walk-in wants a brake-pad swap (45 min). Hoist 1 is on the logbook all morning; hoist 2 has a 30-minute gap at 10:00.',
      bookaburraResponse:
        'Picker only offers slots with a full 45 minutes on a free hoist. The 30-minute gap is never shown; lands him on 11:30.',
    },
    {
      time: '10:30',
      event:
        'A customer opens the picker for a second wheel-alignment today.',
      bookaburraResponse:
        '11:30 isn’t offered — the alignment bay is single-occupancy and already booked 11:00–11:45. Earliest free alignment slot shown is 14:00.',
    },
    {
      time: '12:15',
      event:
        'A fleet customer drops a van in mid-day. Logbook service estimated at 2 hours.',
      bookaburraResponse:
        'Booking blocks 2 hours on the next available hoist. Doesn’t let the afternoon stack like a 45-minute job.',
    },
    {
      time: '13:30',
      event:
        'Follow-up list flags 7 regulars overdue for their 6-monthly logbook.',
      bookaburraResponse:
        'One batch of magic-link "time for your logbook?" SMS goes out. Two self-book into Thursday and Friday morning slots by 5pm.',
    },
    {
      time: '15:00',
      event:
        'A part doesn’t turn up for tomorrow’s 8am logbook. Customer is notified via the workshop’s text.',
      bookaburraResponse:
        'Customer clicks magic-link reschedule from his confirmation, picks the same slot on Friday. Hoist frees up tomorrow for a waiting brake job.',
    },
  ],
  pains: [
    {
      quote:
        'A 2-hour logbook got booked into a slot with only 45 minutes free before the next car needed the hoist.',
      explanation:
        'The booking page has to know how long each job actually takes, not just when the day starts.',
    },
    {
      quote:
        'Our regulars’ 6-monthly logbooks slip through the cracks — we only remember them when they ring up.',
      explanation:
        'A workshop should know who’s overdue before the customer does.',
    },
    {
      quote:
        'Two wheel-off jobs got booked on the same morning. Both needed the alignment bay. One had to wait.',
      explanation:
        'Single-occupancy resources need to be enforced by the calendar, not by memory.',
    },
  ],
  solutions: [
    {
      title: 'Service-duration-aware slots',
      body: (
        <>
          A logbook gets 2 hours. A brake pad gets 45 minutes. Slots reflect the
          actual job — not a fixed grid.
        </>
      ),
    },
    {
      title: 'Resource matching',
      body: (
        <>
          The alignment bay is single-occupancy. The booking page never offers
          two alignments at the same time.
        </>
      ),
    },
    {
      title: 'Follow-up list',
      body: (
        <>
          See which regulars are overdue for their next logbook service, based
          on the service’s recommended frequency.
        </>
      ),
    },
    {
      title: 'Magic-link reschedule',
      body: (
        <>
          When a part doesn’t arrive in time, the customer can move their
          own slot from the link in their reminder — no phone tag.
        </>
      ),
    },
  ],
  industrySlug: 'mechanics',
};
